<script setup lang="ts">
import { save } from "ionicons/icons";
import { reactive } from "vue";
import {
  IonInput,
  IonPage,
  IonButton,
  IonNote,
  IonLoading,
  IonFooter,
  IonContent,
  IonList,
  IonItem,
  IonBackButton,
  IonHeader,
  IonToolbar,
  IonLabel,
  IonIcon,
  IonTitle,
  IonButtons,
  useIonRouter,
  onIonViewWillEnter,
} from "@ionic/vue";
import { useWindowWidth } from "@/composition/window";
import { useForm } from "../..";
import { addEvent } from "@/api/events";
import { useContextStore } from "@/stores/context";
import { useToasters } from "@/composition/toasters";
import { useUserStore } from "@/stores/user";
import { watch } from "vue";
import { event as gtag_event } from "vue-gtag";

const { isMobile } = useWindowWidth();
const state = reactive({
  start_date_open: false,
  end_date_open: false,
  loading: false,
  errors: {
    name: [],
    slug: [],
    capacity: [],
    start_date: [],
    end_date: [],
    timezone: [],
  },
});

const form = useForm();
const router = useIonRouter();
const contextStore = useContextStore();
const userStore = useUserStore();
const toasters = useToasters();

// const emptyFieldsPresent = computed(
//   () =>
//     form.event.name === "" ||
//     form.event.start_date === "" ||
//     form.event.end_date === "" ||
//     form.event.capacity === 0 ||
//     form.event.timezone_id === ""
// );

onIonViewWillEnter(() => {
  gtag_event("event_form_page_access", {
    category: "check-in app",
    label: "Event form page access",
  });
});

const createEvent = () => {
  state.loading = true;
  state.errors = {
    name: [],
    slug: [],
    capacity: [],
    start_date: [],
    end_date: [],
    timezone: [],
  };

  form.event.client_account = userStore.related.clients[0].id;

  addEvent(form.event)
    .then((response) => {
      const event = response.data;

      gtag_event("event_created", {
        category: "check-in app",
        label: "New event created",
      });

      contextStore.getEvent(event.id).then(() => {
        state.loading = false;
        userStore.fetchRoles();
        router.push(`/event/${contextStore.event.id}/home`);
      });
    })
    .catch((err) => {
      if (Array.isArray(err.response.data)) {
        toasters.addError(err.response.data[0]);

        const limits_achieved_msg =
          "Maximum capacity or maximum number of events exceeded.";

        if (err.response.data[0] === limits_achieved_msg) {
          gtag_event("limits_achieved", {
            category: "check-in app",
            label: "Limits achieved while trying to create a event",
          });
        }
      } else {
        state.errors = { ...state.errors, ...err.response.data };
      }
      state.loading = false;
    });
};

watch(
  () => form.event.start_date,
  (start_date) => {
    if (start_date) {
      let date_obj = new Date(start_date);
      date_obj.setDate(date_obj.getDate() + 1);
      form.event.end_date = date_obj.toISOString().slice(0, -8);
    }
  }
);
</script>

<template>
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button color="white"
            :default-href="userStore.userIsLoggedIn ? '/home' : '/onboarding'"></ion-back-button>
        </ion-buttons>
        <ion-title>New event</ion-title>

        <!-- <ion-progress-bar :value="0.75"></ion-progress-bar> -->
      </ion-toolbar>
    </ion-header>

    <ion-content v-if="isMobile">
      <div class="container">
        <h1>Basic information</h1>
        <p>Be descriptive and name your event.</p>
      </div>
      <ion-list>
        <ion-item :class="{
          'ion-invalid':
            state.errors.name.length > 0 || state.errors.slug.length > 0,
        }">
          <ion-label position="stacked">Name</ion-label>
          <ion-input v-model="form.event.name" placeholder="My event"></ion-input>
          <ion-note slot="error" v-for="(message, i) in state.errors.name" :key="`name-error-${i}`">
            {{ message }}
          </ion-note>
          <ion-note slot="error" v-if="state.errors.slug.length > 0">
            This name is already in use. Please try another one.
          </ion-note>
        </ion-item>
        <!-- <ion-item :class="{ 'ion-invalid': state.errors.slug.length > 0 }">
          <ion-label position="stacked">Slug</ion-label>
          <ion-input
            v-model="form.event.slug"
            placeholder="my-event"
          ></ion-input>
          <ion-note
            slot="error"
            v-for="(message, i) in state.errors.slug"
            :key="`slug-error-${i}`"
          >
            {{ message }}
          </ion-note>
        </ion-item> -->
        <ion-item>
          <ion-label position="stacked">Location</ion-label>
          <ion-input v-model="form.event.location" placeholder="New York"></ion-input>
        </ion-item>

        <ion-item :class="{ 'ion-invalid': state.errors.capacity.length > 0 }">
          <ion-label position="stacked">Capacity</ion-label>
          <ion-input v-model="form.event.capacity" type="number" placeholder="1000"></ion-input>
          <ion-note slot="error" v-for="(message, i) in state.errors.capacity" :key="`capacity-error-${i}`">
            {{ message }}
          </ion-note>
        </ion-item>
      </ion-list>
      <div class="container">
        <h1>Date and time</h1>
        <p>Set when your event starts and ends.</p>
      </div>

      <ion-list>
        <ion-item :class="{ 'ion-invalid': state.errors.start_date.length > 0 }">
          <ion-label position="stacked"> Start date </ion-label>
          <ion-input type="datetime-local" v-model="form.event.start_date">
          </ion-input>
          <ion-note slot="error" v-for="(message, i) in state.errors.start_date" :key="`start_date-error-${i}`">
            {{ message }}
          </ion-note>
        </ion-item>

        <ion-item :class="{ 'ion-invalid': state.errors.end_date.length > 0 }">
          <ion-label position="stacked">End date</ion-label>
          <ion-input type="datetime-local" v-model="form.event.end_date">
          </ion-input>
          <ion-note slot="error" v-for="(message, i) in state.errors.end_date" :key="`end_date-error-${i}`">
            {{ message }}
          </ion-note>
        </ion-item>

        <!-- <ion-item :class="{ 'ion-invalid': state.errors.timezone.length > 0 }">
          <ion-label position="stacked">Timezone</ion-label>
          <ion-select
            placeholder="Select timezone"
            interface="popover"
            v-model="form.event.timezone_id"
          >
            <ion-select-option value="Europe/Lisbon">
              Europe/Lisbon
            </ion-select-option>
            <ion-select-option value="Europe/London">
              Europe/London
            </ion-select-option>
            <ion-select-option value="Europe/Madrid">
              Europe/Madrid
            </ion-select-option>
            <ion-select-option value="Europe/Berlin">
              Europe/Berlin
            </ion-select-option>
          </ion-select>
          <ion-note
            slot="error"
            v-for="(message, i) in state.errors.timezone"
            :key="`timezone-error-${i}`"
          >
            {{ message }}
          </ion-note>
        </ion-item> -->
      </ion-list>
    </ion-content>
    <div class="colored-bg full-height" v-else>
      <div class="container">
        <div class="flex-desktop">
          <div class="full-width">
            <h1>Basic information</h1>
            <p>Be descriptive and name your event.</p>
            <ion-list>
              <ion-item :class="{
                'ion-invalid':
                  state.errors.name.length > 0 ||
                  state.errors.slug.length > 0,
              }">
                <ion-label position="stacked">Name</ion-label>
                <ion-input v-model="form.event.name" placeholder="My event"></ion-input>
                <ion-note slot="error" v-for="(message, i) in state.errors.name" :key="`name-error-${i}`">
                  {{ message }}
                </ion-note>
                <ion-note slot="error" v-if="state.errors.slug.length > 0">
                  This name is already in use. Please try another one.
                </ion-note>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Location</ion-label>
                <ion-input v-model="form.event.location" placeholder="New York"></ion-input>
              </ion-item>

              <ion-item :class="{ 'ion-invalid': state.errors.capacity.length > 0 }">
                <ion-label position="stacked">Capacity</ion-label>
                <ion-input v-model="form.event.capacity" type="number" placeholder="1000"></ion-input>
                <ion-note slot="error" v-for="(message, i) in state.errors.capacity" :key="`capacity-error-${i}`">
                  {{ message }}
                </ion-note>
              </ion-item>
            </ion-list>
          </div>
          <div class="full-width">
            <h1>Date and time</h1>
            <p>Set when your event starts and ends.</p>
            <ion-list>
              <ion-item :class="{ 'ion-invalid': state.errors.start_date.length > 0 }">
                <ion-label position="stacked"> Start date </ion-label>
                <ion-input type="datetime-local" v-model="form.event.start_date">
                </ion-input>
                <ion-note slot="error" v-for="(message, i) in state.errors.start_date" :key="`start_date-error-${i}`">
                  {{ message }}
                </ion-note>
              </ion-item>

              <ion-item :class="{ 'ion-invalid': state.errors.end_date.length > 0 }">
                <ion-label position="stacked">End date</ion-label>
                <ion-input type="datetime-local" v-model="form.event.end_date">
                </ion-input>
                <ion-note slot="error" v-for="(message, i) in state.errors.end_date" :key="`end_date-error-${i}`">
                  {{ message }}
                </ion-note>
              </ion-item>
            </ion-list>
          </div>
        </div>
        <div style="width: 300px; margin: 50px auto 30px auto">
          <ion-button expand="block" color="primary" @click="createEvent()">
            <ion-icon slot="start" :icon="save"></ion-icon>
            Save
          </ion-button>
        </div>
      </div>
    </div>

    <ion-footer v-if="isMobile">
      <div class="container">
        <section>
          <ion-button expand="block" color="primary" @click="createEvent()">
            <ion-icon slot="start" :icon="save"></ion-icon>
            Save
          </ion-button>
        </section>
      </div>
    </ion-footer>
    <ion-loading :is-open="state.loading" message="Please wait...">
    </ion-loading>
  </ion-page>
</template>

<style scoped lang="scss">
.container .flex-desktop {
  justify-content: space-between;
}

.container .flex-desktop div {
  margin: 0 80px;
}
</style>
